/** Indian numbering (thousand, lakh, crore, …) — shared across calculators. */

const UNITS = [
  '',
  'one',
  'two',
  'three',
  'four',
  'five',
  'six',
  'seven',
  'eight',
  'nine',
  'ten',
  'eleven',
  'twelve',
  'thirteen',
  'fourteen',
  'fifteen',
  'sixteen',
  'seventeen',
  'eighteen',
  'nineteen',
];

const TENS = [
  '',
  '',
  'twenty',
  'thirty',
  'forty',
  'fifty',
  'sixty',
  'seventy',
  'eighty',
  'ninety',
];

const SCALES = [
  '',
  'thousand',
  'lakh',
  'crore',
  'arab',
  'kharab',
  'neel',
  'padma',
  'shankh',
];

function convertTwoDigits(n) {
  if (n === 0) return '';
  if (n < 20) return UNITS[n];
  return TENS[Math.floor(n / 10)] + (n % 10 !== 0 ? `-${UNITS[n % 10]}` : '');
}

function convertThreeDigits(n) {
  if (n === 0) return '';
  if (n < 100) return convertTwoDigits(n);
  const remainder = n % 100;
  return (
    `${UNITS[Math.floor(n / 100)]} hundred ` +
    (remainder !== 0 ? convertTwoDigits(remainder) : '')
  );
}

export function capitalizeSentence(str) {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * Full Indian-notation wording (can be long). Prefer {@link inrAmountInWordsShort} for UI.
 * @param {number} num
 * @returns {string}
 */
export function numberToWordsIndian(num) {
  if (num === 0) return 'zero';
  if (num < 0) return `minus ${numberToWordsIndian(Math.abs(num))}`;

  let word = '';

  let chunk = num % 1000;
  if (chunk !== 0) {
    word = convertThreeDigits(chunk).trim();
  }
  let rest = Math.floor(num / 1000);

  let scaleIndex = 1;

  while (rest > 0) {
    chunk = rest % 100;
    if (chunk !== 0) {
      const chunkWord = convertTwoDigits(chunk).trim();
      word = `${chunkWord} ${SCALES[scaleIndex]}${word ? ` ${word}` : ''}`;
    }
    rest = Math.floor(rest / 100);
    scaleIndex++;
  }

  return word.replace(/\s+/g, ' ').trim();
}

/**
 * Decompose n &gt; 0 into Indian place-value segments high → low.
 * @param {number} n
 * @returns {{ pairs: { scaleIndex: number, value: number }[], low3: number }}
 */
function decomposeIndianPositive(n) {
  const low3 = n % 1000;
  const pairs = [];
  let rest = Math.floor(n / 1000);
  let scaleIndex = 1;
  while (rest > 0) {
    const pair = rest % 100;
    if (pair !== 0) {
      pairs.push({ scaleIndex, value: pair });
    }
    rest = Math.floor(rest / 100);
    scaleIndex += 1;
  }
  pairs.reverse();
  return { pairs, low3 };
}

function pairSegmentWords({ scaleIndex, value }) {
  const w = convertTwoDigits(value).trim();
  return `${w} ${SCALES[scaleIndex]}`.trim();
}

function wordsSubThousandExact(n) {
  if (n === 0) return 'zero';
  if (n < 100) return convertTwoDigits(n).trim();
  return convertThreeDigits(n).replace(/\s+/g, ' ').trim();
}

/**
 * @param {number} n non-negative integer
 */
function inrAmountInWordsShortNonNegative(n) {
  if (n === 0) return capitalizeSentence('zero');

  const { pairs, low3 } = decomposeIndianPositive(n);
  let raw = '';

  if (pairs.length >= 2) {
    const a = pairSegmentWords(pairs[0]);
    const b = pairSegmentWords(pairs[1]);
    raw = `${a} ${b}`.trim();
    const approx = low3 !== 0 || pairs.length > 2;
    raw = approx ? `${raw} approx.` : raw;
  } else if (pairs.length === 1) {
    const first = pairSegmentWords(pairs[0]);
    if (low3 === 0) {
      raw = first;
    } else {
      const h = Math.floor(low3 / 100) * 100;
      const teu = low3 % 100;
      let second = '';
      let approx = false;
      if (h > 0) {
        second = convertThreeDigits(h).replace(/\s+/g, ' ').trim();
        approx = teu !== 0;
      } else if (teu > 0) {
        second = convertTwoDigits(teu).trim();
      }
      raw = second ? `${first} ${second}`.trim() : first;
      if (approx) raw = `${raw} approx.`;
    }
  } else {
    raw = wordsSubThousandExact(low3);
  }

  return capitalizeSentence(raw.replace(/\s+/g, ' ').trim());
}

/**
 * Short INR wording for UI: at most two “levels” (e.g. crore + lakh, or thousand + hundreds),
 * then " approx." when lower detail is omitted. Use everywhere amounts are shown in words.
 * @param {number} num
 * @returns {string}
 */
export function inrAmountInWordsShort(num) {
  if (!Number.isFinite(num)) return '';
  const n = Math.round(Math.abs(num));
  if (num < 0) {
    const pos = inrAmountInWordsShortNonNegative(n);
    return pos ? `Minus ${pos.charAt(0).toLowerCase() + pos.slice(1)}` : '';
  }
  return inrAmountInWordsShortNonNegative(n);
}
